import { ALBUM_SORTINGS, ITEM_TYPES } from "../utils/constants/enumConstants";

// #region Item types

export const buildFullItemId = (itemId, itemType) => {
    return `spotify:${itemType}:${itemId}`;
}

export const getItemType = (itemId) => {
    const splits = itemId.split(':');

    if(splits.length !== 3)
    {
        return undefined;
    }

    return splits[1];
}

// #endregion Item types

// #region Simplify items

export const simplifySavedAlbums = (albumItems) => {    
    return albumItems.map((albumItem) => simplifySavedAlbum(albumItem));
}

export const simplifySavedAlbum = (albumItem) => {
    const simplifiedAlbum = simplifyAlbum(albumItem.album);

    return {
        ...simplifiedAlbum, 
        addedAt: albumItem.added_at,
        sortIndexes: albumItem.sortIndexes
    };
}

export const simplifyAlbum = (album) => {
    return {
        artist: album.artists[0].name,
        id: album.id,
        images: simplifyItemImages(album.images),
        name: album.name,
        totalTracks: album.total_tracks,
        uri: album.uri,
        type: album.album_type,
        inQueue: false
    }
}

export const simplifyPlayableSingleItem = item => {
    if(!item) {
        return undefined;
    }

    if(item.type === ITEM_TYPES.track) {
        return simplifyTrack(item);
    }
    
    if(item.type === ITEM_TYPES.episode) {
        return simplifyEpisode(item);
    }

    return undefined;
}

export const simplifyTrack = item => {
    if(!item || item.type !== ITEM_TYPES.track)
    {
        return undefined;
    }

    return {
        artists: item.artists.map((artist) => artist.name),
        id: item.id,
        images: simplifyItemImages(item.album.images),
        name: item.name
    }
}

export const simplifyEpisode = item => {
    if(!item || item.type !== ITEM_TYPES.episode)
    {
        return undefined;
    }

    return {
        artists: [item.show.name],
        id: item.id,
        images: simplifyItemImages(item.images),
        name: item.name
    }
}

export const simplifyItemImages = (images) => {
    return simplifyImages(images);
}

export const simplifyUserProfile = (userProfile) => {
    return {
        name: userProfile.display_name,
        images: simplifyProfileImages(userProfile.images)
    };
}

export const simplifyProfileImages = (images) => {
    const sizes = ['md', 'sm', 'xs', 'xxs'];

    return simplifyImages(images, sizes);
}

const simplifyImages = (images, sizes = ['lg', 'md', 'sm', 'xs', 'xxs']) => {
    var imagesBySizes = {};
    sizes.forEach((size, index) => {
        if(index >= images.length) return;
        imagesBySizes[size] = images[index];
    });

    return imagesBySizes;
}

// #endregion

// #region Sorting albums

const getNewSortIndexes = (order, index) => {
    let sortIndexes = {};

    switch(order) {
        case ALBUM_SORTINGS.playedRecently:
            sortIndexes.playedRecently = index;
            break;
        case ALBUM_SORTINGS.addedRecently:
            sortIndexes.addedRecently = index;
            break;
        case ALBUM_SORTINGS.alphabetical:
            sortIndexes.alphabetical = index;
            break;
        case ALBUM_SORTINGS.creator:
            sortIndexes.creator = index;
            break;
        default:
            break;
    }

    return sortIndexes;
}

const checkSortIndexSaved = (firstAlbumSortIndexes, order) => {
    if(!firstAlbumSortIndexes) return false;

    switch(order)
    {
        case ALBUM_SORTINGS.playedRecently:
            return firstAlbumSortIndexes.playedRecently !== undefined;
        case ALBUM_SORTINGS.addedRecently:
            return firstAlbumSortIndexes.addedRecently !== undefined;
        case ALBUM_SORTINGS.alphabetical:
            return firstAlbumSortIndexes.alphabetical !== undefined;
        case ALBUM_SORTINGS.creator:
            return firstAlbumSortIndexes.creator !== undefined;
        default:
            return false;
    }
}

export const saveCurrentSortingOnAlbums = (albums, order) => {
    if(!albums || albums.length === 0) return albums;

    if(checkSortIndexSaved(albums[0].sortIndexes, order))
    {
        return albums;
    }

    return albums.map((album, index) => ({...album, sortIndexes: {...album.sortIndexes, ...getNewSortIndexes(order, index)}}));
}

export const sortSavedAlbums = (albums, order) => {
    const compareBySortIndex = checkSortIndexSaved(albums[0].sortIndexes, order);

    const sortByCreator = (albumA, albumB) => 
    {
        const creatorA = compareBySortIndex ? albumA.sortIndexes.creator : albumA.artist.toLowerCase();
        const creatorB = compareBySortIndex ? albumB.sortIndexes.creator : albumB.artist.toLowerCase();

        return creatorA < creatorB ? -1 : 1
    }

    const sortByName = (albumA, albumB) => 
    {
        const nameA = compareBySortIndex ? albumA.sortIndexes.alphabetical : albumA.name.toLowerCase();
        const nameB = compareBySortIndex ? albumB.sortIndexes.alphabetical : albumB.name.toLowerCase()

        if(nameA === nameB)
        {
            return sortByCreator(albumA, albumB);
        }

        return nameA < nameB ? -1 : 1;
    }

    const sortByAddedDate = (albumA, albumB) => {
        const addedDateA = compareBySortIndex ? albumA.sortIndexes.addedRecently : albumA.addedAt;
        const addedDateB = compareBySortIndex ? albumB.sortIndexes.addedRecently : albumB.addedAt;

        if(addedDateA === addedDateB)
        {
            return sortByName(albumA, albumB);
        }

        if(compareBySortIndex)
        {
            return addedDateA < addedDateB ? -1 : 1;
        }

        return addedDateA < addedDateB ? 1 : -1;
    }

    const sortByPlayedDate = (albumA, albumB) => {
        if(!compareBySortIndex)
        {
            return 1;
        }

        const sortIndexA = albumA.sortIndexes.playedRecently;
        const sortIndexB = albumB.sortIndexes.playedRecently;

        return sortIndexA < sortIndexB ? -1 : 1;
    }

    if(!order || order === ALBUM_SORTINGS.playedRecently)
    {
        return albums.toSorted((albumA, albumB) => sortByPlayedDate(albumA, albumB));
    }

    if(order === ALBUM_SORTINGS.addedRecently)
    {
        return albums.toSorted((albumA, albumB) => sortByAddedDate(albumA, albumB));
    }

    if(order === ALBUM_SORTINGS.alphabetical)
    {
        return albums.toSorted((albumA, albumB) => sortByName(albumA, albumB));
    }

    if(order === ALBUM_SORTINGS.creator)
    {
        return albums.toSorted((albumA, albumB) => sortByCreator(albumA, albumB));
    }
}

// #endregion Sorting albums

// #region Filtering albums

export const areAlbumFiltersEmpty = (filters) => {
    const { types, textPattern } = filters;

    return isTypeFilterEmpty(types) && isTextFilterEmpty(textPattern);
}

const isTypeFilterEmpty = (types) => {
    return !types || types.length === 0;
}

const isTextFilterEmpty = (textPattern) => {
    return !textPattern;
}

export const filterAlbums = (albums, filters) => {
    const filterAlbumByType = (album, types) => {
        if(isTypeFilterEmpty(types)) return true;
    
        return types.includes(album.type)
    }
    
    const filterAlbumByText = (album, textPattern) => {
        if(isTextFilterEmpty(textPattern)) return true;
    
        return album.name.toLowerCase().includes(textPattern.toLowerCase()) || album.artist.toLowerCase().includes(textPattern.toLowerCase());
    }

    if(areAlbumFiltersEmpty(filters)) return albums;

    const { types, textPattern } = filters;

    return albums.filter((album) => filterAlbumByType(album, types) && filterAlbumByText(album, textPattern));
}

// #endregion Filtering albums

// #region Other methods

export const getPlayItemOffset = (value) => {
    if(!isNaN(+value))
    {
        return {
            position: value
        }
    }

    return {
        uri: value
    }
}

// #endregion